// journal/format — the Journal's words for periods and instants: "Idag,
// torsdag 1 oktober", "September 2026", "Kvartal 3, juli till september
// 2026", a summary's "16:40" or "30 sep. 17:05". Deterministic over the shell's
// clock (`nowMs`) and the active locale; no `Date.now()` here.

import { useLingui } from "@lingui/react/macro";
import type { PeriodKey } from "../../app/ports/journal.js";

export type JournalFormat = {
    readonly heading: (key: PeriodKey) => string;
    // A rollup's child, without "Today," (it reads as a list of dates).
    readonly childHeading: (key: PeriodKey) => string;
    // An instant as a clock time on its own day, else with its date.
    readonly when: (iso: string) => string;
    // An instant's date ("29 september"), unabbreviated so it can end a
    // sentence.
    readonly date: (iso: string) => string;
    // A day's activity time ("14:05").
    readonly time: (iso: string) => string;
};

const capitalize = (s: string): string =>
    s.charAt(0).toLocaleUpperCase() + s.slice(1);

// A calendar date as UTC midnight, so formatting never shifts it a day.
const utc = (year: number, month: number, day = 1): Date =>
    new Date(Date.UTC(year, month - 1, day));

const sameDay = (a: Date, b: Date): boolean =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

export const useJournalFormat = (nowMs: number): JournalFormat => {
    const { t, i18n } = useLingui();
    const { locale } = i18n;
    const now = new Date(nowMs);
    const fmt = (options: Intl.DateTimeFormatOptions) =>
        new Intl.DateTimeFormat(locale, options);
    const dayLong = fmt({
        weekday: "long",
        day: "numeric",
        month: "long",
        timeZone: "UTC",
    });
    const dayLongYear = fmt({
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone: "UTC",
    });
    const monthYear = fmt({ month: "long", year: "numeric", timeZone: "UTC" });
    const clock = fmt({ hour: "2-digit", minute: "2-digit" });
    const shortDate = fmt({ day: "numeric", month: "short" });
    const longDate = fmt({ day: "numeric", month: "long" });

    const heading = (key: PeriodKey, relative: boolean): string => {
        switch (key.scale) {
            case "day": {
                const d = utc(key.year, key.month, key.day);
                const local = new Date(key.year, key.month - 1, key.day);
                const text = (
                    key.year === now.getFullYear() ? dayLong : dayLongYear
                ).format(d);
                const yesterday = new Date(nowMs);
                yesterday.setDate(yesterday.getDate() - 1);
                if (relative && sameDay(local, now)) return t`Today, ${text}`;
                if (relative && sameDay(local, yesterday))
                    return t`Yesterday, ${text}`;
                return capitalize(text);
            }
            case "month":
                return capitalize(monthYear.format(utc(key.year, key.month)));
            case "year":
                return String(key.year);
        }
    };

    return {
        heading: (key) => heading(key, true),
        childHeading: (key) => heading(key, false),
        when: (iso) => {
            const d = new Date(iso);
            if (Number.isNaN(d.getTime())) return "";
            return sameDay(d, now)
                ? clock.format(d)
                : `${shortDate.format(d)} ${clock.format(d)}`;
        },
        date: (iso) => {
            const d = new Date(iso);
            return Number.isNaN(d.getTime()) ? "" : longDate.format(d);
        },
        time: (iso) => {
            const d = new Date(iso);
            return Number.isNaN(d.getTime()) ? "" : clock.format(d);
        },
    };
};
